import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Crown, Sparkles, TrendingUp, Award, Globe2, FileText, Landmark, Building2,
  ShieldCheck, Radar, Network, ChevronRight, Download, Zap, ClipboardCheck,
  Handshake, Banknote, Users, BookOpen, AlertTriangle,
} from "lucide-react";

/**
 * Cadastre360™ Phase XIV — National Executive Decision Intelligence™
 * Presentation-only cockpit. Consumes existing enterprise services via the
 * new /api/cadastre360/executive/* additive router. Zero regression.
 */
const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const authHeaders = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Chip = ({ children, color = "#4FA3D1", filled = false }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

const Bar = ({ label, score, target, accent }) => (
  <div className="flex items-center gap-2 py-1 text-[12px]">
    <div className="w-52 truncate text-[#F6F8FA]">{label}</div>
    <div className="flex-1 h-1.5 rounded-sm bg-white/5 relative">
      <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${score}%`, background: accent }} />
      {target ? <div className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-white/70" style={{ left: `${target}%` }} /> : null}
    </div>
    <div className="w-12 text-right font-mono text-[11px] text-[#F6F8FA]">{score}</div>
  </div>
);

const UrgencyBadge = ({ u }) => {
  const color = u === "high" ? RED : u === "medium" ? AMBER : GREEN;
  return <Chip color={color} filled>{u}</Chip>;
};

export const NationalExecutiveIntelligence = () => {
  const [caps, setCaps] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [nesrep, setNesrep] = useState(null);
  const [bench, setBench] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [fed, setFed] = useState(null);
  const [demoEnv, setDemoEnv] = useState(null);
  const [compAnalytics, setCompAnalytics] = useState(null);
  const [audience, setAudience] = useState("cabinet");
  const [focus, setFocus] = useState("Phase XIV endorsement");
  const [briefings, setBriefings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, m, n, b, o, f, d, ca, bl] = await Promise.all([
        axios.get(`${API}/cadastre360/executive/capabilities`, { headers: authHeaders() }),
        axios.get(`${API}/cadastre360/executive/live-metrics`, { headers: authHeaders() }),
        axios.get(`${API}/cadastre360/executive/nesrep`, { headers: authHeaders() }),
        axios.get(`${API}/cadastre360/executive/benchmarking`, { headers: authHeaders() }),
        axios.get(`${API}/cadastre360/executive/outcome-mapping`, { headers: authHeaders() }),
        axios.get(`${API}/cadastre360/executive/federation-status`, { headers: authHeaders() }),
        axios.get(`${API}/cadastre360/executive/demo-environment`, { headers: authHeaders() }),
        axios.get(`${API}/cadastre360/executive/comparative-analytics`, { headers: authHeaders() }),
        axios.get(`${API}/cadastre360/executive/briefings`, { headers: authHeaders() }),
      ]);
      setCaps(c.data); setMetrics(m.data); setNesrep(n.data); setBench(b.data);
      setOutcome(o.data); setFed(f.data); setDemoEnv(d.data); setCompAnalytics(ca.data);
      setBriefings(bl.data || []);
    } catch (e) {
      toast.error("Failed to load Phase XIV intelligence — " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const generateBriefing = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/cadastre360/executive/briefings`,
        { audience, focus, period: "current" }, { headers: authHeaders() });
      toast.success(`Executive briefing generated — ${res.data.audience_title}`);
      setBriefings((b) => [res.data, ...b]);
    } catch (e) {
      toast.error("Briefing failed — " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const download = async (path, filename) => {
    try {
      const r = await axios.get(path, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed — " + e.message); }
  };

  const generateValueReport = async () => {
    setBusy(true);
    try {
      await axios.get(`${API}/cadastre360/executive/value-report`, { headers: authHeaders() });
      await download(`${API}/cadastre360/executive/value-report/latest.pdf`, "national-value-report.pdf");
      toast.success("Signed National Value Report™ generated");
    } catch (e) { toast.error("Failed — " + e.message); }
    finally { setBusy(false); }
  };

  const generateStoryline = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/cadastre360/executive/storyline`,
        { subject: focus || "National Resource Governance", audience },
        { headers: authHeaders() });
      toast.success(`Storyline generated — Situation → Recommendation`);
      // Auto-scroll to storyline card
      document.getElementById("storyline-card")?.scrollIntoView({ behavior: "smooth" });
      setStoryline(r.data);
    } catch (e) { toast.error("Storyline failed — " + e.message); }
    finally { setBusy(false); }
  };
  const [storyline, setStoryline] = useState(null);

  const capMetric = metrics?.metrics?.find((m) => m.key === "digital_maturity")?.value ?? 0;

  return (
    <div className="p-6 space-y-4" data-testid="page-executive-intelligence">
      {/* Hero */}
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: PURPLE + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${PURPLE}30 100%)` }}>
        <Crown size={38} className="mt-1" style={{ color: PURPLE }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: PURPLE }}>
            Cadastre360™ · Phase XIV · National Executive Decision Intelligence™
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Sovereign Executive Operating Environment
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Faster, evidence-based, measurable and internationally benchmarked decisions.
            Executive Briefing Engine · Situation Report · Benchmarking · Storyline · Signed Cabinet Artefacts.
            Zero regression · zero duplication · federation ready.
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end">
          <Chip color={GREEN} filled>Cabinet Ready</Chip>
          <Chip color={GOLD} filled>Treasury Ready</Chip>
          <Chip color={BLUE} filled>Investor Ready</Chip>
        </div>
      </div>

      {/* Live National Performance Intelligence */}
      <Card title="National Performance Intelligence™ · Live" accent={GOLD} testId="xiv-live-metrics"
            right={<Chip color={GOLD}>Live · from operational systems</Chip>}>
        {metrics ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {metrics.metrics.map((m) => {
              const c = { gold: GOLD, green: GREEN, teal: TEAL, blue: BLUE, purple: PURPLE }[m.colour] || BLUE;
              const val = m.unit === "USD" ? `$${Number(m.value).toLocaleString()}`
                        : m.unit === "count" ? Number(m.value).toLocaleString()
                        : m.unit === "ha" ? `${Number(m.value).toLocaleString()} ha`
                        : m.unit === "days" ? `${m.value} d`
                        : m.value;
              return (
                <div key={m.key} className="rounded-sm border px-3 py-2" style={{ borderColor: c + "40", background: "rgba(7,27,43,0.55)" }} data-testid={`metric-${m.key}`}>
                  <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#7C93A5]">{m.label}</div>
                  <div className="font-display font-bold mt-1 text-[18px]" style={{ color: c }}>{val}</div>
                  <div className="text-[10px] text-[#B3C2CC] mt-0.5">
                    {m.trend >= 0 ? "▲" : "▼"} {Math.abs(m.trend)}% {m.target ? `· target ${typeof m.target === "number" ? m.target.toLocaleString() : m.target}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        ) : <div className="text-[11px] text-[#7C93A5]">Loading live metrics…</div>}
      </Card>

      {/* Two-column: NESREP + Storyline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="National Executive Situation Report™ (NESREP)" accent={PURPLE} testId="xiv-nesrep"
              right={nesrep ? <Chip color={PURPLE}>{nesrep.highlights?.critical_decisions_today} critical today</Chip> : null}>
          {nesrep ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {[
                  ["Critical today", nesrep.highlights?.critical_decisions_today],
                  ["Provinces need support", nesrep.highlights?.provinces_needing_support],
                  ["Ministries need action", nesrep.highlights?.ministries_needing_intervention],
                  ["AI confidence", `${Math.round((nesrep.highlights?.ai_confidence_average || 0) * 100)}%`],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-sm border px-2 py-1.5" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.45)" }}>
                    <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{l}</div>
                    <div className="text-[15px] font-bold" style={{ color: PURPLE }}>{v ?? "—"}</div>
                  </div>
                ))}
              </div>
              <div className="max-h-[360px] overflow-auto pr-1 space-y-1.5">
                {nesrep.answers.map((a, i) => (
                  <div key={i} className="rounded-sm border p-2" style={{ borderColor: "rgba(123,47,190,0.25)", background: "rgba(7,27,43,0.45)" }}>
                    <div className="flex items-start gap-2">
                      <div className="text-[12px] text-[#F6F8FA] flex-1">{a.question}</div>
                      <UrgencyBadge u={a.urgency} />
                    </div>
                    <div className="text-[10.5px] text-[#B3C2CC] mt-1">
                      {a.responsible_ministry} · {a.suggested_timeline} · confidence {Math.round(a.confidence_score * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading situation report…</div>}
        </Card>

        {/* Storyline card */}
        <Card title="Executive Storyline Generator™" accent={BLUE} testId="xiv-storyline"
              right={<Chip color={BLUE}>Situation → Recommendation</Chip>}>
          <div id="storyline-card" className="space-y-2">
            <div className="flex gap-2 flex-wrap">
              <input value={focus} onChange={(e) => setFocus(e.target.value)}
                     className="flex-1 min-w-[220px] rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15"
                     placeholder="Subject / theme" data-testid="storyline-subject" />
              <select value={audience} onChange={(e) => setAudience(e.target.value)}
                      className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15"
                      data-testid="storyline-audience">
                {(caps?.audience_profiles || []).map((p) => (
                  <option key={p.key} value={p.key}>{p.title}</option>
                ))}
              </select>
              <button onClick={generateStoryline} disabled={busy}
                      className="rounded-sm border px-3 py-1.5 text-[11.5px] font-medium"
                      style={{ borderColor: BLUE + "60", color: "#F6F8FA", background: BLUE + "25" }}
                      data-testid="btn-generate-storyline">
                <Sparkles size={12} className="inline mr-1" /> Generate
              </button>
            </div>
            {storyline ? (
              <div className="rounded-sm border p-2 space-y-1.5" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.55)" }}>
                {[
                  ["Situation", storyline.situation],
                  ["Challenge", storyline.challenge],
                  ["Evidence", storyline.evidence],
                  ["Impact", storyline.impact],
                  ["Value Created", storyline.value_created],
                  ["Transformation", storyline.transformation],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="font-mono uppercase tracking-[0.16em] text-[9.5px] text-[#7C93A5]">{k}</div>
                    <div className="text-[12px] text-[#F6F8FA]">{v}</div>
                  </div>
                ))}
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1">
                  <div>
                    <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Recommendations</div>
                    <ul className="text-[11.5px] text-[#F6F8FA] list-disc pl-4">
                      {storyline.recommendations?.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">Expected outcomes</div>
                    <ul className="text-[11.5px] text-[#F6F8FA] list-disc pl-4">
                      {storyline.expected_outcomes?.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            ) : <div className="text-[11px] text-[#7C93A5]">Enter a subject and generate a Cabinet-grade storyline.</div>}
          </div>
        </Card>
      </div>

      {/* Executive Briefing Engine */}
      <Card title="National Executive Briefing Engine™ · 12 audience profiles" accent={PURPLE} testId="xiv-briefing-engine">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={audience} onChange={(e) => setAudience(e.target.value)}
                  className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15"
                  data-testid="briefing-audience">
            {(caps?.audience_profiles || []).map((p) => (
              <option key={p.key} value={p.key}>{p.title}</option>
            ))}
          </select>
          <input value={focus} onChange={(e) => setFocus(e.target.value)}
                 className="flex-1 min-w-[220px] rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15"
                 placeholder="Focus (optional)" data-testid="briefing-focus" />
          <button onClick={generateBriefing} disabled={busy}
                  className="rounded-sm border px-3 py-1.5 text-[11.5px] font-medium"
                  style={{ borderColor: PURPLE + "60", color: "#F6F8FA", background: PURPLE + "25" }}
                  data-testid="btn-generate-briefing">
            <Sparkles size={12} className="inline mr-1" /> Generate briefing
          </button>
          <button onClick={generateValueReport} disabled={busy}
                  className="rounded-sm border px-3 py-1.5 text-[11.5px] font-medium"
                  style={{ borderColor: GOLD + "60", color: "#F6F8FA", background: GOLD + "25" }}
                  data-testid="btn-value-report">
            <Award size={12} className="inline mr-1" /> Signed Value Report™
          </button>
        </div>

        <div className="mt-3 max-h-[280px] overflow-auto space-y-1.5">
          {briefings.length === 0 && <div className="text-[11px] text-[#7C93A5]">No briefings yet — generate one above.</div>}
          {briefings.map((b) => (
            <div key={b.id} className="rounded-sm border p-2 flex items-center gap-2"
                 style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.45)" }}>
              <FileText size={12} style={{ color: PURPLE }} />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-[#F6F8FA] truncate">{b.audience_title} — {b.focus || "General"}</div>
                <div className="text-[10px] text-[#7C93A5] font-mono">{b.generated_at?.slice(0, 19).replace("T", " ")} UTC · SHA-256 {b.digital_signature_sha256?.slice(0, 16)}…</div>
              </div>
              <button onClick={() => download(`${API}/cadastre360/executive/briefings/${b.id}.pdf`, `briefing-${b.id}.pdf`)}
                      className="rounded-sm border px-2 py-1 text-[10.5px]"
                      style={{ borderColor: PURPLE + "50", color: PURPLE }}
                      data-testid={`dl-pdf-${b.id}`}>
                <Download size={10} className="inline mr-1" /> PDF
              </button>
              <button onClick={() => download(`${API}/cadastre360/executive/briefings/${b.id}.docx`, `briefing-${b.id}.docx`)}
                      className="rounded-sm border px-2 py-1 text-[10.5px]"
                      style={{ borderColor: BLUE + "50", color: BLUE }}
                      data-testid={`dl-docx-${b.id}`}>
                <Download size={10} className="inline mr-1" /> DOCX
              </button>
              <button onClick={() => download(`${API}/cadastre360/executive/briefings/${b.id}.pptx`, `briefing-${b.id}.pptx`)}
                      className="rounded-sm border px-2 py-1 text-[10.5px]"
                      style={{ borderColor: GOLD + "50", color: GOLD }}
                      data-testid={`dl-pptx-${b.id}`}>
                <Download size={10} className="inline mr-1" /> PPTX
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Benchmarking + Comparative */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title={`Global Government Benchmarking™ · readiness ${bench?.readiness_score ?? "—"}`} accent={GREEN} testId="xiv-benchmarking"
              right={<Chip color={GREEN}>{bench?.items?.length || 0} frameworks</Chip>}>
          {bench?.items?.map((f) => (
            <Bar key={f.key} label={`${f.label} (peer ${f.peer})`} score={f.score} target={f.peer} accent={GREEN} />
          )) || <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
          {bench?.improvement_areas?.length ? (
            <div className="mt-3 border-t pt-2" style={{ borderColor: GREEN + "25" }}>
              <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5] mb-1">Improvement recommendations</div>
              <ul className="text-[11.5px] text-[#F6F8FA] list-disc pl-5">
                {bench.improvement_areas.map((a) => <li key={a.key}>{a.label} — score {a.score}</li>)}
              </ul>
            </div>
          ) : null}
        </Card>

        <Card title="Comparative Transformation Analytics™" accent={BLUE} testId="xiv-comparative">
          {compAnalytics ? (
            <>
              <div className="text-[10.5px] text-[#7C93A5] mb-1 font-mono uppercase tracking-[0.14em]">Previous · Current · 2026 · 2030</div>
              {compAnalytics.series.map((s) => (
                <div key={s.pillar} className="flex items-center gap-2 py-1 text-[12px]">
                  <div className="w-28 text-[#F6F8FA]">{s.pillar}</div>
                  <div className="flex-1 flex gap-1 items-end h-6">
                    {[s.previous, s.current, s.target_2026, s.vision_2030].map((v, i) => (
                      <div key={i} className="flex-1 rounded-sm relative" style={{ background: [PURPLE, BLUE, GREEN, GOLD][i], height: `${v}%`, opacity: 0.85 }} title={`${v}`}>
                        <div className="absolute -top-3 left-0 right-0 text-center font-mono text-[9px] text-[#F6F8FA]">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
                {compAnalytics.peers.map((p) => (
                  <div key={p.country} className="rounded-sm border px-2 py-1" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.45)" }}>
                    <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{p.country}</div>
                    <div className="text-[13px] font-bold text-[#F6F8FA]">{p.score}</div>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>
      </div>

      {/* National Outcome Mapping */}
      <Card title="National Outcome Mapping™ · KPI → Ministry → Legislation → Vision" accent={GOLD} testId="xiv-outcome-map">
        <div className="overflow-x-auto">
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="text-left text-[#7C93A5] font-mono uppercase text-[9.5px] tracking-[0.14em]">
                <th className="py-1 pr-2">KPI</th>
                <th className="py-1 pr-2">Ministry</th>
                <th className="py-1 pr-2">Department</th>
                <th className="py-1 pr-2">Legislation</th>
                <th className="py-1 pr-2">Vision 2030</th>
                <th className="py-1 pr-2">NDS</th>
                <th className="py-1 pr-2">SDG</th>
                <th className="py-1">AU</th>
              </tr>
            </thead>
            <tbody>
              {outcome?.items?.map((r, i) => (
                <tr key={i} className="text-[#F6F8FA] border-t border-white/5">
                  <td className="py-1 pr-2">{r.kpi}</td>
                  <td className="py-1 pr-2 text-[#B3C2CC]">{r.ministry}</td>
                  <td className="py-1 pr-2 text-[#B3C2CC]">{r.department}</td>
                  <td className="py-1 pr-2 text-[#B3C2CC]">{r.legislation}</td>
                  <td className="py-1 pr-2 text-[#B3C2CC]">{r.vision2030}</td>
                  <td className="py-1 pr-2 text-[#B3C2CC]">{r.nds}</td>
                  <td className="py-1 pr-2"><Chip color={GREEN}>{r.sdg}</Chip></td>
                  <td className="py-1"><Chip color={GOLD}>{r.au}</Chip></td>
                </tr>
              )) || null}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Federation + Demo env */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Federation Awareness & Deployment Intelligence™" accent={PURPLE} testId="xiv-federation">
          {fed ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                {[
                  ["Installed", fed.counts?.installed, GREEN],
                  ["Active", fed.counts?.active, BLUE],
                  ["Reserved", fed.counts?.reserved, AMBER],
                  ["Unavailable", fed.counts?.unavailable, RED],
                ].map(([l, v, c]) => (
                  <div key={l} className="rounded-sm border px-2 py-1.5" style={{ borderColor: c + "40", background: "rgba(7,27,43,0.5)" }}>
                    <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{l}</div>
                    <div className="font-bold text-[16px]" style={{ color: c }}>{v}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {fed.platforms.map((p) => {
                  const c = p.status === "installed" ? GREEN : p.status === "active" ? BLUE : p.status === "reserved" ? AMBER : RED;
                  return (
                    <div key={p.key} className="rounded-sm border px-2 py-1 flex items-center gap-2"
                         style={{ borderColor: c + "30", background: "rgba(7,27,43,0.45)" }}>
                      <div className="w-1 h-4 rounded-sm" style={{ background: c }} />
                      <div className="flex-1 text-[12px] text-[#F6F8FA]">{p.label}</div>
                      <Chip color={c}>{p.status}</Chip>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
                {Object.entries(fed.health || {}).slice(0, 6).map(([k, v]) => (
                  <div key={k} className="rounded-sm border px-2 py-1" style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.45)" }}>
                    <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{k}</div>
                    <div className="text-[12px] text-[#F6F8FA]">{String(v)}</div>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>

        <Card title="National Demonstration Environment™" accent={GREEN} testId="xiv-demo-env">
          {demoEnv ? (
            <>
              <div className="text-[11.5px] text-[#DCE3E8] mb-2">{demoEnv.notice}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                {Object.entries(demoEnv.contents || {}).map(([k, v]) => (
                  <div key={k} className="rounded-sm border px-2 py-1" style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                    <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{k}</div>
                    <div className="text-[13px] font-bold text-[#F6F8FA]">{typeof v === "number" ? v.toLocaleString() : v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-1 flex-wrap">
                {demoEnv.audiences.map((a) => <Chip key={a} color={GREEN}>{a}</Chip>)}
              </div>
            </>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>
      </div>

      {/* Capabilities catalogue */}
      {caps && (
        <Card title="Phase XIV Capability Catalogue" accent={GOLD} testId="xiv-capabilities">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
            {caps.capabilities.map((c) => (
              <div key={c.key} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.45)" }}>
                <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{c.section}</div>
                <div className="text-[12px] text-[#F6F8FA]">{c.label}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1 flex items-center gap-3">
        <span>Cadastre360™ · Phase XIV · Executive Decision Intelligence</span>
        <span>·</span>
        <span>zero regression · zero duplication · federation ready · production ready</span>
      </div>
    </div>
  );
};

export default NationalExecutiveIntelligence;
